
import { useState, useEffect, useCallback } from 'react';
import type { SharedUserSummary } from '@/lib/shared-types';
import type { UserSelectOption } from '@/lib/types/user-select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UserSelect } from '@/components/ui/user-select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { useToast } from '@/lib/hooks/use-toast';
import { api } from '@/lib/api/client';
import {
  Users,
  UserPlus,
  UserMinus,
  Search,
  Shield,
  Clock,
  RefreshCw
} from 'lucide-react';

interface StreamAccessRecord {
  userId: number;
  accessType: 'direct' | 'code' | 'delegated';
  grantedAt: string;
  grantedBy: number;
  userName?: string;
  userEmail?: string;
}

interface Stream {
  id: number;
  title: string;
  status: string;
  inviteRequired: boolean;
  visibility?: 'public' | 'members' | 'invite_only';
}

type User = SharedUserSummary;

interface StreamAccessManagementProps {
  stream?: Stream;
  onClose?: () => void;
}

export function StreamAccessManagement({ stream, onClose }: StreamAccessManagementProps) {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [accessList, setAccessList] = useState<StreamAccessRecord[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<UserSelectOption[]>([]);
  const [accessType, setAccessType] = useState<'direct' | 'delegated'>('direct');
  const [isGrantDialogOpen, setIsGrantDialogOpen] = useState(false);
  const [isRevoking, setIsRevoking] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchAccessList = useCallback(async () => {
    if (!stream) return;

    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get<{ access: StreamAccessRecord[] }>(`api/streams/${stream.id}/access`);

      if (response.success && response.data) {
        setAccessList(response.data.access || []);
      } else {
        setError(response.error?.message || 'Failed to load access list');
        console.error('Fetch access list error:', response.error);
      }
    } catch (err) {
      setError('Failed to load access list');
      console.error('Fetch access list error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [stream]);

  useEffect(() => {
    if (stream) {
      fetchAccessList();
    }
  }, [stream, fetchAccessList]);

  const handleGrantAccess = async () => {
    if (!stream || selectedUsers.length === 0) return;

    setIsLoading(true);
    try {
      // Grant access to multiple users
      const grantPromises = selectedUsers.map(user =>
        api.post(`api/streams/${stream.id}/access/grant`, {
          userId: user.id,
          accessType
        })
      );

      const results = await Promise.allSettled(grantPromises);
      const successful = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.length - successful;

      if (successful > 0) {
        toast({
          title: 'Access Granted',
          description: `Successfully granted access to ${successful} user${successful > 1 ? 's' : ''}${failed > 0 ? ` (${failed} failed)` : ''}.`
        });

        setIsGrantDialogOpen(false);
        setSelectedUsers([]);
        fetchAccessList();
      } else {
        toast({
          title: 'Error',
          description: 'Failed to grant access to any users',
          variant: 'destructive'
        });
      }
    } catch (err: unknown) {
      const error = err as { message?: string };
      toast({
        title: 'Error',
        description: error.message || 'Failed to grant access',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRevokeAccess = async (userId: number) => {
    if (!stream) return;

    setIsRevoking(userId);
    try {
      const response = await api.delete(`api/streams/${stream.id}/access/${userId}`);

      if (response.success) {
        toast({
          title: 'Access Revoked',
          description: 'User access has been revoked.'
        });

        fetchAccessList();
      } else {
        toast({
          title: 'Error',
          description: response.error?.message || 'Failed to revoke access',
          variant: 'destructive'
        });
      }
    } catch (err) {
      console.error('Revoke access error:', err);
      toast({
        title: 'Error',
        description: 'Failed to revoke access',
        variant: 'destructive'
      });
    } finally {
      setIsRevoking(null);
    }
  };

  const filteredAccessList = accessList.filter(record => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      record.userName?.toLowerCase().includes(query) ||
      record.userEmail?.toLowerCase().includes(query) ||
      record.userId.toString().includes(query)
    );
  });

  const getAccessTypeBadge = (type: string) => {
    switch (type) {
      case 'direct':
        return <Badge variant="default">Direct</Badge>;
      case 'code':
        return <Badge variant="secondary">Invite Code</Badge>;
      case 'delegated':
        return <Badge variant="outline">Delegated</Badge>;
      default:
        return <Badge variant="outline">{type}</Badge>;
    }
  };

  if (!stream) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Stream Access Management
          </CardTitle>
          <CardDescription>
            Select a stream to manage access permissions.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Access Management
            </CardTitle>
            <CardDescription>
              Managing access for: {stream.title}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchAccessList}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Refresh
            </Button>
            <Dialog open={isGrantDialogOpen} onOpenChange={setIsGrantDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <UserPlus className="h-4 w-4 mr-1" />
                  Grant Access
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Grant Stream Access</DialogTitle>
                  <DialogDescription>
                    Grant a user access to &quot;{stream.title}&quot;
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <label htmlFor="user" className="text-sm font-medium">
                      Select Users
                    </label>
                    <UserSelect
                      multiple
                      value={selectedUsers.map(u => u.id)}
                      onChange={setSelectedUsers}
                      placeholder="Search and select users..."
                      excludeUsers={accessList.map(a => a.userId)}
                      allowCreateUser={true}
                      streamId={stream?.id}
                      accessType={accessType}
                      onUserCreated={(user) => {
                        toast({
                          title: 'User Created',
                          description: `User ${user.email} has been created and granted access.`,
                        });
                      }}
                    />
                  </div>
                  <div className="grid gap-2">
                    <label htmlFor="type" className="text-sm font-medium">
                      Access Type
                    </label>
                    <Select value={accessType} onValueChange={(v) => setAccessType(v as 'direct' | 'delegated')}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="direct">Direct Access</SelectItem>
                        <SelectItem value="delegated">Delegated Access</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsGrantDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleGrantAccess} disabled={selectedUsers.length === 0 || isLoading}>
                    {isLoading ? <LoadingSpinner className="h-4 w-4 mr-1" /> : null}
                    Grant Access to {selectedUsers.length > 0 ? `${selectedUsers.length} User${selectedUsers.length > 1 ? 's' : ''}` : 'Users'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Stats */}
        <div className="flex items-center gap-4 mb-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            <Users className="h-4 w-4" />
            <span>{accessList.length} users with access</span>
          </div>
          {stream.visibility ? (
            <Badge variant="outline">
              {stream.visibility === 'public' ? 'Public' : stream.visibility === 'invite_only' ? 'Invite Only' : 'Members Only'}
            </Badge>
          ) : stream.inviteRequired && (
            <Badge variant="outline">Invite Only</Badge>
          )}
        </div>

        {/* Access List Table */}
        {isLoading && accessList.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : filteredAccessList.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {searchQuery ? 'No users match your search' : 'No users have access to this stream yet'}
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Access Type</TableHead>
                  <TableHead>Granted</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAccessList.map((record) => (
                  <TableRow key={record.userId}>
                    <TableCell>
                      <div>
                        <div className="font-medium">
                          {record.userName || `User #${record.userId}`}
                        </div>
                        {record.userEmail && (
                          <div className="text-sm text-muted-foreground">
                            {record.userEmail}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {getAccessTypeBadge(record.accessType)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        {new Date(record.grantedAt).toLocaleDateString()}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleRevokeAccess(record.userId)}
                        disabled={isRevoking === record.userId}
                      >
                        {isRevoking === record.userId ? (
                          <LoadingSpinner className="h-4 w-4" />
                        ) : (
                          <>
                            <UserMinus className="h-4 w-4 mr-1" />
                            Revoke
                          </>
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
